import React, { useEffect, useState } from "react";
import {
    Download, Monitor, Server, Copy, Check, Star, Cpu, HardDrive,
    FileArchive, ArrowRight, RefreshCw, Terminal,
} from "lucide-react";
import { toast } from "sonner";
import { http, formatBytes, apiBaseUrl, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Downloads — redesigned platform cards only (page shell, sidebar, header,
 * verification banner, auth, and the existing signed-download / entitlement
 * flow are untouched; see PortalLayout for the surrounding chrome).
 *
 * Per-platform requirements/architecture/package-type strings below are
 * static real facts about each concrete installer (filename extension,
 * OS support) sourced from backend/routers/desktop_releases.py's
 * PLATFORMS dict and backend/routers/agent_updates.py's OS_ASSETS dict —
 * not fabricated per-device data. size_bytes and checksum always come
 * from the live API response.
 */
const DESKTOP_META = {
    windows: {
        requirements: ["Windows 10 or later", "(64-bit)"],
        architecture: "64-bit",
        // Real filename: MoovBytesDesktopSetup.exe (desktop_releases.py PLATFORMS)
        packageType: ".exe installer",
        recommended: true,
    },
    macos: {
        requirements: ["macOS 11 (Big Sur) or later", "(Apple Silicon or Intel)"],
        architecture: "Universal",
        // Real filename: MoovBytesDesktop.dmg
        packageType: ".dmg",
    },
    linux: {
        requirements: ["Most modern Linux distributions", "(x86_64)"],
        architecture: "x86_64",
        // Real filename: MoovBytesDesktop.AppImage
        packageType: ".AppImage",
    },
};

const AGENT_META = {
    windows: {
        requirements: ["Windows Server 2016 or later", "(64-bit)"],
        architecture: "64-bit",
        packageType: ".exe installer",
    },
    linux: {
        requirements: ["Most modern Linux distributions", "(x86_64)"],
        architecture: "x86_64",
        packageType: ".tar.gz",
    },
    "linux-deb": {
        requirements: ["Debian / Ubuntu", "(amd64)"],
        architecture: "amd64",
        packageType: ".deb package",
    },
    "linux-rpm": {
        requirements: ["RHEL / Fedora / Rocky / Alma"],
        architecture: "x86_64",
        packageType: ".rpm package",
    },
    macos: {
        // Universal .pkg per agent_updates.py's own comment: "runs on both
        // Apple Silicon and Intel via the same package".
        requirements: ["macOS 11 (Big Sur) or later", "(Apple Silicon or Intel)"],
        architecture: "Universal",
        packageType: ".pkg",
    },
};

// Card header / button name. Windows and macOS have exactly one build
// each (per section), so the bare OS name is unambiguous. The Agent's
// Linux key has three variants -- a generic tarball plus .deb and .rpm
// packages (agent_updates.py's OS_ASSETS) -- and collapsing all three to
// the same bare "Linux" made them look like duplicate cards (three
// identical "Download for Linux" buttons, differing only in a small
// metadata pill below). Desktop's own "linux" key is a single AppImage
// build, not a tarball, so the plain-"linux" case is section-dependent --
// pass isAgent so Desktop's one Linux build still reads as plain "Linux".
function osNameFrom(key, isAgent = false) {
    if (key === "macos") return "macOS";
    if (key === "linux-deb") return "Linux (.deb)";
    if (key === "linux-rpm") return "Linux (.rpm)";
    if (key === "linux") return isAgent ? "Linux (tar.gz)" : "Linux";
    if (key.startsWith("linux")) return "Linux";
    if (key === "windows") return "Windows";
    return key;
}

export default function PortalDownloads() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";
    const [desktop, setDesktop] = useState(null);
    const [agents, setAgents] = useState(null);
    const [desktopErr, setDesktopErr] = useState("");
    const [agentsErr, setAgentsErr] = useState("");
    const [loading, setLoading] = useState(true);
    // Real per-tenant bootstrap credentials (tenant_slug + install_token),
    // used to build a ready-to-run unattended install command so the
    // Agent's tenant slug is genuinely pre-populated rather than left as
    // a generic build-time default every customer would otherwise share.
    // Admin-only (GET /agents/installer/bundle) -- silently omitted for
    // non-admin viewers rather than erroring the whole page.
    const [bundle, setBundle] = useState(null);

    const load = () => {
        setLoading(true);
        setDesktopErr("");
        setAgentsErr("");
        Promise.allSettled([
            http.get("/desktop-releases/assets").then(({ data }) => setDesktop(data)).catch((e) => setDesktopErr(errMsg(e))),
            http.get("/v1/agents/assets").then(({ data }) => setAgents(data)).catch((e) => setAgentsErr(errMsg(e))),
        ]).finally(() => setLoading(false));
        if (isAdmin) {
            http.get("/agents/installer/bundle").then(({ data }) => setBundle(data)).catch(() => {});
        }
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 sm:p-9" data-testid="portal-downloads-page">
            <div className="mb-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                    Get MoovBytes
                </div>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Downloads</h1>
            </div>

            <Section
                title="MoovBytes Desktop"
                subtitle={desktop?.version ? `Version ${desktop.version} — ${desktop.channel}` : "The installed application customers use day to day."}
                kind="desktop"
            >
                {loading ? (
                    <SkeletonGrid />
                ) : desktopErr ? (
                    <ErrorState message={desktopErr} onRetry={load} />
                ) : !desktop || !Object.values(desktop.platforms || {}).some((p) => p.available) ? (
                    <EmptyState label="No MoovBytes Desktop build has been published yet." />
                ) : (
                    <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(desktop.platforms).map(([key, p]) => (
                            <PlatformDownloadCard
                                key={key}
                                productIcon={Monitor}
                                platform={key}
                                name={osNameFrom(key)}
                                fullLabel={p.label}
                                meta={DESKTOP_META[key]}
                                asset={p}
                                notes={desktop.notes}
                                productLabel="MoovBytes Desktop"
                                kind="desktop"
                            />
                        ))}
                    </div>
                )}
            </Section>

            <Section title="MoovBytes Agent" subtitle="For servers and NAS devices you register from inside MoovBytes Desktop." kind="agent">
                {loading ? (
                    <SkeletonGrid />
                ) : agentsErr ? (
                    <ErrorState message={agentsErr} onRetry={load} />
                ) : !agents || !Object.values(agents.assets || {}).some((a) => a.available) ? (
                    <EmptyState label="No Agent installer has been published yet." />
                ) : (
                    <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
                        {Object.entries(agents.assets)
                            .filter(([, a]) => a.available)
                            .map(([key, a]) => (
                                <PlatformDownloadCard
                                    key={key}
                                    productIcon={Server}
                                    platform={key}
                                    name={osNameFrom(key, true)}
                                    fullLabel={a.label}
                                    meta={AGENT_META[key]}
                                    asset={a}
                                    notes={agents.notes}
                                    productLabel="MoovBytes Agent"
                                    kind="agent"
                                    installCommand={key === "windows" && bundle
                                        ? `MoovByteAgentSetup.exe /VERYSILENT /TenantSlug=${bundle.tenant_slug} /InstallToken=${bundle.install_token}`
                                        : null}
                                />
                            ))}
                    </div>
                )}
            </Section>
        </div>
    );
}

const SECTION_BANNER = {
    // Install-mistake guardrail — this exact copy exists because a real
    // customer installed Desktop on a server, mistaking it for Agent, when
    // the two cards looked visually near-identical. Every card also carries
    // its own badge (see PlatformDownloadCard) so the distinction survives
    // even if someone skips straight past this banner.
    desktop: {
        icon: Monitor,
        text: "Install this on the computer you use day to day. It's an interactive app with its own sign-in screen. For a server or NAS, use MoovBytes Agent below instead.",
    },
    agent: {
        icon: Server,
        text: "Runs silently in the background on servers and NAS devices — no sign-in screen, no desktop UI to open. Don't install this on your own PC; use MoovBytes Desktop above instead.",
    },
};

function Section({ title, subtitle, kind, children }) {
    const banner = SECTION_BANNER[kind];
    return (
        <div className="mb-9">
            <div className="mb-3">
                <h2 className="font-display text-[21px] font-bold">{title}</h2>
                <p className="text-[13px] text-muted-foreground">{subtitle}</p>
            </div>
            {banner && (
                <div className="mb-4 flex items-start gap-2.5 rounded-md border border-brand/25 bg-[#EEF4FF] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-foreground/80 dark:bg-brand/10">
                    <banner.icon size={15} className="mt-0.5 shrink-0 text-brand" />
                    <span>{banner.text}</span>
                </div>
            )}
            {children}
        </div>
    );
}

function EmptyState({ label }) {
    return (
        <div className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground">
            {label}
        </div>
    );
}

function ErrorState({ message, onRetry }) {
    return (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground">
            <span>Unable to load this release. {message}</span>
            <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted/40"
            >
                <RefreshCw size={12} /> Retry
            </button>
        </div>
    );
}

function SkeletonGrid() {
    return (
        <div className="grid grid-cols-1 gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
                <div key={i} className="min-h-[258px] animate-pulse rounded-[10px] border border-border bg-surface p-[18px]">
                    <div className="flex gap-3.5">
                        <div className="h-16 w-16 shrink-0 rounded-xl bg-muted/60" />
                        <div className="flex-1 space-y-2 pt-1">
                            <div className="h-4 w-2/3 rounded bg-muted/60" />
                            <div className="h-3 w-full rounded bg-muted/40" />
                            <div className="h-3 w-1/2 rounded bg-muted/40" />
                        </div>
                    </div>
                    <div className="mt-3.5 flex gap-2">
                        <div className="h-7 w-16 rounded-full bg-muted/40" />
                        <div className="h-7 w-16 rounded-full bg-muted/40" />
                        <div className="h-7 w-20 rounded-full bg-muted/40" />
                    </div>
                    <div className="mt-4 h-[42px] rounded-md bg-muted/50" />
                    <div className="mt-2.5 h-10 rounded-md bg-muted/30" />
                </div>
            ))}
        </div>
    );
}

/** Windows / Apple / Tux mark — small inline SVGs (no icon-package addition
 * just for three OS logos, per the redesign brief). Apple reuses lucide's
 * existing Apple glyph tinted to spec; Windows/Tux are bundled here. */
function WindowsMark({ size = 20 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 88 88" fill="none" aria-hidden="true">
            <path fill="#1473E6" d="M0 12.4 35.9 7.5v34.7H0V12.4Zm40.1-5.6L88 0v41.1H40.1V6.8ZM0 46.3h35.9V81L0 76v-29.7Zm40.1 0H88V88L40.4 81.2 40.1 46.3Z" />
        </svg>
    );
}

// Official Linux/Tux glyph (Simple Icons, CC0) — the accurate mark, not a
// hand-drawn approximation.
function TuxMark({ size = 22 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="#0b0b0b" aria-hidden="true">
            <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139zm.529 3.405h.013c.213 0 .396.062.584.198.19.135.33.332.438.533.105.259.158.459.166.724 0-.02.006-.04.006-.06v.105a.086.086 0 01-.004-.021l-.004-.024a1.807 1.807 0 01-.15.706.953.953 0 01-.213.335.71.71 0 00-.088-.042c-.104-.045-.198-.064-.284-.133a1.312 1.312 0 00-.22-.066c.05-.06.146-.133.183-.198.053-.128.082-.264.088-.402v-.02a1.21 1.21 0 00-.061-.4c-.045-.134-.101-.2-.183-.333-.084-.066-.167-.132-.267-.132h-.016c-.093 0-.176.03-.262.132a.8.8 0 00-.205.334 1.18 1.18 0 00-.09.4v.019c.002.089.008.179.02.267-.193-.067-.438-.135-.607-.202a1.635 1.635 0 01-.018-.2v-.02a1.772 1.772 0 01.15-.768c.082-.22.232-.406.43-.533a.985.985 0 01.594-.2zm-2.962.059h.036c.142 0 .27.048.399.135.146.129.264.288.344.465.09.199.14.4.153.667v.004c.007.134.006.2-.002.266v.08c-.03.007-.056.018-.083.024-.152.055-.274.135-.393.2.012-.09.013-.18.003-.267v-.015c-.012-.133-.04-.2-.082-.333a.613.613 0 00-.166-.267.248.248 0 00-.183-.064h-.021c-.071.006-.13.04-.186.132a.552.552 0 00-.12.27.944.944 0 00-.023.33v.015c.012.135.037.2.08.334.046.134.098.2.166.268.01.009.02.018.034.024-.07.057-.117.07-.176.136a.304.304 0 01-.131.068 2.62 2.62 0 01-.275-.402 1.772 1.772 0 01-.155-.667 1.759 1.759 0 01.08-.668 1.43 1.43 0 01.283-.535c.128-.133.26-.2.418-.2zm1.37 1.706c.332 0 .733.065 1.216.399.293.2.523.269 1.052.468h.003c.255.136.405.266.478.399v-.131a.571.571 0 01.016.47c-.123.31-.516.643-1.063.842v.002c-.268.135-.501.333-.775.465-.276.135-.588.292-1.012.267a1.139 1.139 0 01-.448-.067 3.566 3.566 0 01-.322-.198c-.195-.135-.363-.332-.612-.465v-.005h-.005c-.4-.246-.616-.512-.686-.71-.07-.268-.005-.47.193-.6.224-.135.38-.271.483-.336.104-.074.143-.102.176-.131h.002v-.003c.169-.202.436-.47.839-.601.139-.036.294-.065.466-.065zm2.8 2.142c.358 1.417 1.196 3.475 1.735 4.473.286.534.855 1.659 1.102 3.024.156-.005.33.018.513.064.646-1.671-.546-3.467-1.089-3.966-.22-.2-.232-.335-.123-.335.59.534 1.365 1.572 1.646 2.757.13.535.16 1.104.021 1.67.067.028.135.06.205.067 1.032.534 1.413.938 1.23 1.537v-.043c-.06-.003-.12 0-.18 0h-.016c.151-.467-.182-.825-1.065-1.224-.915-.4-1.646-.336-1.77.465-.008.043-.013.066-.018.135-.068.023-.139.053-.209.064-.43.268-.662.669-.793 1.187-.13.533-.17 1.156-.205 1.869v.003c-.02.334-.17.838-.319 1.35-1.5 1.072-3.58 1.538-5.348.334a2.645 2.645 0 00-.402-.533 1.45 1.45 0 00-.275-.333c.182 0 .338-.03.465-.067a.615.615 0 00.314-.334c.108-.267 0-.697-.345-1.163-.345-.467-.931-.995-1.788-1.521-.63-.4-.986-.87-1.15-1.396-.165-.534-.143-1.085-.015-1.645.245-1.07.873-2.11 1.274-2.763.107-.065.037.135-.408.974-.396.751-1.14 2.497-.122 3.854a8.123 8.123 0 01.647-2.876c.564-1.278 1.743-3.504 1.836-5.268.048.036.217.135.289.202.218.133.38.333.59.465.21.201.477.335.876.335.039.003.075.006.11.006.412 0 .73-.134.997-.268.29-.134.52-.334.74-.4h.005c.467-.135.835-.402 1.044-.7zm2.185 8.958c.037.6.343 1.245.882 1.377.588.134 1.434-.333 1.791-.765l.211-.01c.315-.007.577.01.847.268l.003.003c.208.199.305.53.391.876.085.4.154.78.409 1.066.486.527.645.906.636 1.14l.003-.007v.018l-.003-.012c-.015.262-.185.396-.498.595-.63.401-1.746.712-2.457 1.57-.618.737-1.37 1.14-2.036 1.191-.664.053-1.237-.2-1.574-.898l-.005-.003c-.21-.4-.12-1.025.056-1.69.176-.668.428-1.344.463-1.897.037-.714.076-1.335.195-1.814.12-.465.308-.797.641-.984l.045-.022zm-10.814.049h.01c.053 0 .105.005.157.014.376.055.706.333 1.023.752l.91 1.664.003.003c.243.533.754 1.064 1.189 1.637.434.598.77 1.131.729 1.57v.006c-.057.744-.48 1.148-1.125 1.294-.645.135-1.52.002-2.395-.464-.968-.536-2.118-.469-2.857-.602-.369-.066-.61-.2-.723-.4-.11-.2-.113-.602.123-1.23v-.004l.002-.003c.117-.334.03-.752-.027-1.118-.055-.401-.083-.71.043-.94.16-.334.396-.4.69-.533.294-.135.64-.202.915-.47h.002v-.002c.256-.268.445-.601.668-.838.19-.201.38-.336.663-.336zm7.159-9.074c-.435.201-.945.535-1.488.535-.542 0-.97-.267-1.28-.466-.154-.134-.28-.268-.373-.335-.164-.134-.144-.333-.074-.333.109.016.129.134.199.2.096.066.215.2.36.333.292.2.68.467 1.167.467.485 0 1.053-.267 1.398-.466.195-.135.445-.334.648-.467.156-.136.149-.267.279-.267.128.016.034.134-.147.332a8.097 8.097 0 01-.69.468zm-1.082-1.583V5.64c-.006-.02.013-.042.029-.05.074-.043.18-.027.26.004.063 0 .16.067.15.135-.006.049-.085.066-.135.066-.055 0-.092-.043-.141-.068-.052-.018-.146-.008-.163-.065zm-.551 0c-.02.058-.113.049-.166.066-.047.025-.086.068-.14.068-.05 0-.13-.02-.136-.068-.01-.066.088-.133.15-.133.08-.031.184-.047.259-.005.019.009.036.03.03.05v.02h.003z" />
        </svg>
    );
}

const PLATFORM_ICON = {
    windows: { El: WindowsMark, bg: "bg-[#EEF4FF] dark:bg-[#1a2a4a]" },
    macos: { El: null, bg: "bg-[#EEF4FF] dark:bg-[#1a2a4a]" }, // handled inline below (lucide Apple)
    linux: { El: TuxMark, bg: "bg-[#FFF5E6] dark:bg-[#3a2c14]" },
    "linux-deb": { El: TuxMark, bg: "bg-[#FFF5E6] dark:bg-[#3a2c14]" },
    "linux-rpm": { El: TuxMark, bg: "bg-[#FFF5E6] dark:bg-[#3a2c14]" },
};

function PlatformIcon({ platform }) {
    const cfg = PLATFORM_ICON[platform] || { El: WindowsMark, bg: "bg-[#EEF4FF] dark:bg-[#1a2a4a]" };
    return (
        <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-xl ${cfg.bg}`}>
            {platform === "macos" ? (
                <AppleGlyph size={34} />
            ) : (
                <cfg.El size={platform.startsWith("linux") ? 38 : 34} />
            )}
        </div>
    );
}

// Official Apple glyph (Simple Icons, CC0), tinted navy per the redesign
// brief (§9) — not a hand-drawn approximation.
function AppleGlyph({ size = 34 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="#1D4ED8" aria-hidden="true">
            <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
        </svg>
    );
}

function MetadataPill({ icon: Icon, label }) {
    return (
        <span className="inline-flex h-[29px] items-center gap-1.5 rounded-full bg-muted/50 px-[11px] text-[11.5px] font-medium text-foreground/80">
            <Icon size={13} />
            {label}
        </span>
    );
}

const KIND_BADGE = {
    desktop: { label: "Interactive app · sign-in required", cls: "bg-[#EEF4FF] text-[#1D4ED8] dark:bg-[#1a2a4a] dark:text-[#8ab4ff]" },
    agent: { label: "Background service · no sign-in", cls: "bg-muted text-muted-foreground" },
};

function PlatformDownloadCard({ productIcon: ProductIcon, platform, name, fullLabel, meta, asset, notes, productLabel, kind, installCommand }) {
    const [copied, setCopied] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    const [cmdCopied, setCmdCopied] = useState(false);

    const copyCommand = async () => {
        try {
            await navigator.clipboard.writeText(installCommand);
            setCmdCopied(true);
            toast.success("Copied");
            setTimeout(() => setCmdCopied(false), 1500);
        } catch {
            toast.error("Clipboard unavailable — copy manually");
        }
    };
    // Always use the backend's own download_url -- for Desktop it embeds
    // the required expires/sig HMAC (POST /desktop-releases/assets signs
    // it fresh on every load), which a hand-built query string can't
    // reproduce. Agent downloads don't need a signature but this still
    // works for them since the backend returns the equivalent URL either way.
    const url = asset.download_url ? `${apiBaseUrl()}${asset.download_url}` : null;
    const requirements = meta?.requirements || [];
    const architecture = meta?.architecture;
    const packageType = meta?.packageType;

    const copyChecksum = async () => {
        try {
            await navigator.clipboard.writeText(asset.checksum || "");
            setCopied(true);
            toast.success("Copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Clipboard unavailable — copy manually");
        }
    };

    return (
        <div
            className="flex min-h-[258px] flex-col rounded-[10px] border border-border bg-surface p-[18px] shadow-[0_1px_2px_rgba(16,24,40,0.02)] transition hover:border-foreground/20"
            data-testid={`portal-download-card-${fullLabel.replace(/\s+/g, "-").toLowerCase()}`}
        >
            {/* Header: icon + name/requirements + recommended badge */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex gap-3.5">
                    <PlatformIcon platform={platform} />
                    <div className="pt-0.5">
                        <div className="font-display text-[17px] font-bold leading-tight text-foreground">{name}</div>
                        {requirements.map((line, i) => (
                            <div key={i} className="text-[12.5px] leading-snug text-muted-foreground">{line}</div>
                        ))}
                        {KIND_BADGE[kind] && (
                            <span className={`mt-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${KIND_BADGE[kind].cls}`}>
                                {KIND_BADGE[kind].label}
                            </span>
                        )}
                    </div>
                </div>
                {meta?.recommended && asset.available && (
                    <span className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full bg-[#EEF4FF] px-2.5 text-[11px] font-semibold text-[#1D4ED8] dark:bg-[#1a2a4a] dark:text-[#8ab4ff]">
                        <Star size={12} className="fill-current" /> Recommended
                    </span>
                )}
            </div>

            {asset.available ? (
                <>
                    {/* Metadata pills */}
                    <div className="mt-3.5 flex flex-wrap gap-2">
                        {architecture && <MetadataPill icon={Cpu} label={architecture} />}
                        <MetadataPill icon={HardDrive} label={formatBytes(asset.size_bytes)} />
                        {packageType && <MetadataPill icon={FileArchive} label={packageType} />}
                    </div>

                    {/* Actions pinned to the bottom so cards align across a row */}
                    <div className="mt-auto pt-4">
                        <a
                            href={url}
                            data-testid={`portal-download-link-${fullLabel.replace(/\s+/g, "-").toLowerCase()}`}
                            aria-label={`Download ${productLabel} for ${name}`}
                            className="flex h-[42px] w-full items-center justify-center gap-2 rounded-md bg-brand text-xs font-semibold text-white transition hover:opacity-90 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand/40"
                        >
                            <Download size={15} /> Download for {name}
                        </a>

                        {installCommand && (
                            <div className="mt-2.5 rounded-md border border-brand/25 bg-[#EEF4FF] p-2.5 dark:bg-brand/10">
                                <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-brand">
                                    <Terminal size={11} /> Recommended: unattended install
                                </div>
                                <p className="mb-1.5 text-[10.5px] leading-snug text-muted-foreground">
                                    After downloading, run this from an elevated Command Prompt — your tenant and install token are already baked in.
                                </p>
                                <div className="flex items-center gap-1.5 rounded bg-[#0b0f1a] px-2 py-1.5">
                                    <code className="min-w-0 flex-1 truncate font-mono text-[10px] text-[#d1d5db]" title={installCommand}>
                                        {installCommand}
                                    </code>
                                    <button
                                        onClick={copyCommand}
                                        aria-label={`Copy unattended install command for ${name}`}
                                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-white/80 hover:bg-white/10"
                                    >
                                        {cmdCopied ? <Check size={13} /> : <Copy size={13} />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {asset.checksum && (
                            <div className="mt-2.5 flex h-10 items-center gap-2 rounded-md border border-border bg-muted/30 px-3">
                                <span className="shrink-0 text-[11px] font-medium text-muted-foreground">SHA-256</span>
                                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground" title={asset.checksum}>
                                    {asset.checksum}
                                </span>
                                <button
                                    onClick={copyChecksum}
                                    aria-label={`Copy ${name} SHA-256 checksum`}
                                    className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded text-brand hover:bg-muted/60 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand/40"
                                >
                                    {copied ? <Check size={15} /> : <Copy size={15} />}
                                </button>
                            </div>
                        )}

                        {notes && (
                            <div className="mt-2.5">
                                <button
                                    onClick={() => setNotesOpen((v) => !v)}
                                    aria-label={`View release notes for ${productLabel} ${name}`}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                                >
                                    Release notes <ArrowRight size={12} />
                                </button>
                                {notesOpen && (
                                    <p className="mt-1.5 rounded-md bg-muted/30 p-2.5 text-[11.5px] leading-relaxed text-muted-foreground">
                                        {notes}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="mt-auto pt-4">
                    <div className="rounded-md border border-dashed border-border px-3 py-2.5 text-center text-[11px] text-muted-foreground">
                        Not available — no release currently published for {name}.
                    </div>
                </div>
            )}
        </div>
    );
}
